import { CircleCheckBig, FileText, FolderOpen, Repeat, Truck } from 'lucide-react'

import { Button } from '@/components/ui/button'

import type { WinnerImportResult } from './winner-import.api'
import { doneSummary, formatDuration, formatEuro, minutesSaved } from './winner-import-progress'

type Props = {
  result: WinnerImportResult
  elapsedMs: number
  suppliers: string[]
  onOpenQuote: () => void
  onOpenProject: () => void
  onAnother: () => void
}

/** The payoff: what exists now, how long it took, and what Soly will ask for next. */
export function WinnerImportDone({ result, elapsedMs, suppliers, onOpenQuote, onOpenProject, onAnother }: Props) {
  const created = [
    result.createdCustomer ? `le client ${result.customerName ?? ''}` : null,
    result.createdProject ? `le chantier « ${result.projectName ?? ''} »` : null,
    `le devis (${result.articleCount} lignes)`,
  ].filter(Boolean)

  return (
    <div className="mx-auto flex max-w-2xl flex-col items-center gap-8 py-10 text-center" data-testid="winner-import-done">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 animate-in zoom-in-50 duration-500">
        <CircleCheckBig className="h-9 w-9 text-emerald-600" />
      </div>

      <div className="space-y-2">
        <h3 className="text-2xl font-semibold tracking-tight">Dossier créé</h3>
        <p className="text-muted-foreground">
          Soly a créé {created.join(', ').replace(/, ([^,]*)$/, ' et $1')}.
        </p>
      </div>

      <div className="grid w-full grid-cols-3 divide-x rounded-xl border">
        <Stat value={formatDuration(elapsedMs)} label="du fichier au dossier" />
        <Stat value={`≈ ${minutesSaved(result.articleCount)} min`} label="de saisie évitées" />
        <Stat value={formatEuro(result.totalIncVat)} label="TVAC, au cent près" amount={result.totalIncVat} />
      </div>

      <div className="w-full space-y-3 rounded-xl border bg-slate-50/60 p-5 text-left">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Ensuite</div>
        <div className="flex items-start gap-3 text-sm">
          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
          <span>Relire le devis et l&apos;envoyer au client depuis Soly.</span>
        </div>
        <div className="flex items-start gap-3 text-sm" data-testid="winner-import-done-summary">
          <Truck className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
          <span>
            {doneSummary(result)}
            {suppliers.length > 0 && <> : {suppliers.join(', ')}</>}.
          </span>
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        <Button size="lg" onClick={onOpenQuote} className="gap-2" data-testid="winner-import-open-quote-btn">
          <FileText className="h-4 w-4" />
          Ouvrir le devis
        </Button>
        <Button size="lg" variant="outline" onClick={onOpenProject} className="gap-2" data-testid="winner-import-open-project-btn">
          <FolderOpen className="h-4 w-4" />
          Ouvrir le chantier
        </Button>
        <Button size="lg" variant="ghost" onClick={onAnother} className="gap-2" data-testid="winner-import-another-btn">
          <Repeat className="h-4 w-4" />
          Importer un autre fichier
        </Button>
      </div>
    </div>
  )
}

function Stat({ value, label, amount }: { value: string; label: string; amount?: number }) {
  return (
    <div className="px-3 py-4">
      <div className="text-xl font-semibold tabular-nums" data-amount={amount}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  )
}
